import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

function GhsDiamond({ fill, children }) {
  return (
    <svg viewBox="0 0 64 64" className="h-12 w-12" aria-hidden="true">
      <polygon
        points="32,4 60,32 32,60 4,32"
        fill={fill}
        stroke="#1f2937"
        strokeWidth="2"
      />
      {children}
    </svg>
  );
}

const HAZARD_SVGS = {
  explosive: (
    <GhsDiamond fill="#facc15">
      <circle cx="32" cy="28" r="6" fill="#111827" />
      <path d="M26 36 L32 28 L38 36" fill="none" stroke="#111827" strokeWidth="2" />
      <path d="M20 22 L26 18 M44 22 L38 18 M32 14 V10" stroke="#111827" strokeWidth="2" />
    </GhsDiamond>
  ),
  flammable: (
    <GhsDiamond fill="#f97316">
      <path
        d="M32 18 C28 28 24 32 24 40 C24 46 28 50 32 50 C36 50 40 46 40 40 C40 32 36 28 32 18 Z"
        fill="#111827"
      />
    </GhsDiamond>
  ),
  oxidizing: (
    <GhsDiamond fill="#f59e0b">
      <circle cx="32" cy="32" r="8" fill="#111827" />
      <path
        d="M32 16 V22 M32 42 V48 M16 32 H22 M42 32 H48 M21 21 L25 25 M43 43 L39 39 M43 21 L39 25 M21 43 L25 39"
        stroke="#111827"
        strokeWidth="2"
      />
    </GhsDiamond>
  ),
  corrosive: (
    <GhsDiamond fill="#9ca3af">
      <path d="M20 24 H28 V40 H20 Z M36 24 H44 V40 H36 Z" fill="#111827" />
      <path d="M18 46 H46" stroke="#111827" strokeWidth="3" />
    </GhsDiamond>
  ),
  toxic: (
    <GhsDiamond fill="#111827">
      <circle cx="32" cy="26" r="8" fill="#f8fafc" />
      <path d="M24 38 H40 L36 48 H28 Z" fill="#f8fafc" />
      <circle cx="29" cy="24" r="1.5" fill="#111827" />
      <circle cx="35" cy="24" r="1.5" fill="#111827" />
    </GhsDiamond>
  ),
  harmful: (
    <GhsDiamond fill="#f8fafc">
      <text
        x="32"
        y="40"
        textAnchor="middle"
        fontSize="22"
        fontWeight="700"
        fill="#111827"
      >
        !
      </text>
    </GhsDiamond>
  ),
  irritant: (
    <GhsDiamond fill="#fef3c7">
      <path d="M32 18 L44 46 H20 Z" fill="#111827" />
      <text x="32" y="42" textAnchor="middle" fontSize="14" fill="#fef3c7">
        !
      </text>
    </GhsDiamond>
  ),
  environmental: (
    <GhsDiamond fill="#22c55e">
      <circle cx="32" cy="36" r="10" fill="#052e16" />
      <path d="M20 28 C28 20 36 20 44 28" stroke="#052e16" strokeWidth="3" fill="none" />
    </GhsDiamond>
  ),
};

const hazardSymbols = [
  { id: "explosive", nameKey: "chemical.inventory.hazard.explosive", descKey: "chemical.inventory.hazard.explosive_desc", category: "physical" },
  { id: "flammable", nameKey: "chemical.inventory.hazard.flammable", descKey: "chemical.inventory.hazard.flammable_desc", category: "physical" },
  { id: "oxidizing", nameKey: "chemical.inventory.hazard.oxidizing", descKey: "chemical.inventory.hazard.oxidizing_desc", category: "physical" },
  { id: "corrosive", nameKey: "chemical.inventory.hazard.corrosive", descKey: "chemical.inventory.hazard.corrosive_desc", category: "health" },
  { id: "toxic", nameKey: "chemical.inventory.hazard.toxic", descKey: "chemical.inventory.hazard.toxic_desc", category: "health" },
  { id: "harmful", nameKey: "chemical.inventory.hazard.harmful", descKey: "chemical.inventory.hazard.harmful_desc", category: "health" },
  { id: "irritant", nameKey: "chemical.inventory.hazard.irritant", descKey: "chemical.inventory.hazard.irritant_desc", category: "health" },
  { id: "environmental", nameKey: "chemical.inventory.hazard.environmental", descKey: "chemical.inventory.hazard.environmental_desc", category: "environmental" },
];

export function HazardSymbolIcon({ id, className = "h-10 w-10" }) {
  const icon = HAZARD_SVGS[id];
  if (!icon) return null;
  return <span className={`inline-flex ${className}`}>{icon}</span>;
}

export function HazardSymbolsDisplay({ symbols = [] }) {
  const ids = Array.isArray(symbols) ? symbols : [];
  if (ids.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {ids.map((id) => (
        <HazardSymbolIcon key={id} id={id} />
      ))}
    </div>
  );
}

export const HazardSymbolsSelector = ({ value = [], onChange, label, readOnly = false }) => {
  const { t } = useTranslation();
  const [selectedSymbols, setSelectedSymbols] = useState(value);

  useEffect(() => {
    setSelectedSymbols(value);
  }, [value]);

  const handleSymbolToggle = (symbolId) => {
    if (readOnly) return;
    const newSelection = selectedSymbols.includes(symbolId)
      ? selectedSymbols.filter((id) => id !== symbolId)
      : [...selectedSymbols, symbolId];

    setSelectedSymbols(newSelection);
    onChange?.(newSelection);
  };

  const categories = [
    { id: "physical", name: t("chemical.inventory.hazard.category.physical") },
    { id: "health", name: t("chemical.inventory.hazard.category.health") },
    { id: "environmental", name: t("chemical.inventory.hazard.category.environmental") },
  ];

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700">
        {label || t("chemical.inventory.hazard_symbols")}
      </label>

      {categories.map((category) => (
        <div key={category.id} className="space-y-2">
          <h4 className="text-sm font-medium text-gray-900">{category.name}</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {hazardSymbols
              .filter((symbol) => symbol.category === category.id)
              .map((symbol) => (
                <div
                  key={symbol.id}
                  className={`relative border-2 rounded-lg p-3 transition-colors ${
                    selectedSymbols.includes(symbol.id)
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  } ${readOnly ? "cursor-default" : "cursor-pointer"}`}
                  onClick={() => handleSymbolToggle(symbol.id)}
                >
                  <div className="flex items-center space-x-2">
                    {!readOnly ? (
                      <input
                        type="checkbox"
                        checked={selectedSymbols.includes(symbol.id)}
                        onChange={() => handleSymbolToggle(symbol.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    ) : null}
                    <div className="flex-1">
                      <div className="flex justify-center mb-2">
                        <HazardSymbolIcon id={symbol.id} />
                      </div>
                      <p className="text-xs font-medium text-gray-900 text-center">
                        {t(symbol.nameKey)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1 text-center">
                        {t(symbol.descKey)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
};
